import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import InstagramIcon from "@mui/icons-material/Instagram";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import SignedImage from "./SignedImage";
import useSignedUrl from "../../utils/useSignedUrl";
import ContentReviewChip from "../../components/ContentReviewChip";
import { getHomepageNewsBlocks } from "../../utils/newsItems";
import { COLORS } from "../../theme/tokens";

const PATH_LINE_COLOR = "rgba(60, 143, 153, 0.4)";

function NewsMedia({ content }) {
  if (content.imageUrl) {
    return (
      <Box
        component="img"
        src={content.imageUrl}
        alt={content.imageAlt || content.heading || ""}
        loading="lazy"
        decoding="async"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: content.imageFit || "cover",
          backgroundColor: content.imageBackgroundColor || undefined,
        }}
      />
    );
  }

  return <SignedImage s3Key={content.s3Key} alt={content.imageAlt || content.heading} />;
}

function StepBadge({ label, accent }) {
  const isOrange = accent === "orange";
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        top: -14,
        left: 20,
        zIndex: 2,
        height: 28,
        minWidth: 28,
        px: 1,
        borderRadius: 999,
        backgroundColor: isOrange ? COLORS.orange : COLORS.blue,
        color: isOrange ? COLORS.ink : "#fff",
        border: "3px solid #e7f5f7",
        boxShadow: "0 6px 14px rgba(32, 48, 51, 0.18)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "windroos, Georgia, serif",
        fontSize: "0.86rem",
        lineHeight: 1,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </Box>
  );
}

function NewsHighlight({ block, large = false, lead = false, stepLabel, stepAccent }) {
  const c = block.content || {};
  const { url: documentHref } = useSignedUrl(c.documentS3Key);

  const routeHref = c.articleRoute || c.ctaRoute || c.route;
  const routeIsExternal = Boolean(routeHref && /^https?:\/\//i.test(routeHref));
  const documentRoute = c.documentS3Key ? "/documenten" : null;
  const primaryHref = c.externalUrl || routeHref || documentHref || documentRoute;
  const primaryUsesDocumentRoute =
    Boolean(documentRoute) && !routeHref && !documentHref && !c.externalUrl;
  const primaryIsRoute =
    Boolean(routeHref && !routeIsExternal && !c.externalUrl) ||
    primaryUsesDocumentRoute;
  const primaryTarget = primaryIsRoute ? undefined : primaryHref ? "_blank" : undefined;
  const showImage = Boolean(c.s3Key || c.imageUrl);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        gridRow: large && !lead ? { md: "span 2" } : undefined,
        gridColumn: lead ? "1 / -1" : undefined,
      }}
    >
      {stepLabel && !lead && <StepBadge label={stepLabel} accent={stepAccent} />}
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: lead ? { xs: "column", md: "row" } : "column",
          borderRadius: 1,
          border: "1px solid rgba(32, 48, 51, 0.14)",
          boxShadow: "0 16px 36px rgba(32, 48, 51, 0.1)",
          backgroundColor: "#fff",
        }}
      >
        {showImage && (
          <Box
            sx={{
              aspectRatio: lead ? { xs: "16 / 9", md: "16 / 7" } : "4 / 3",
              overflow: "hidden",
              flexShrink: 0,
              width: lead ? { xs: "100%", md: "55%" } : "100%",
            }}
          >
            <NewsMedia content={c} />
          </Box>
        )}
        <CardContent
          sx={{
            flex: 1,
            p: lead
              ? { xs: 3, md: 4 }
              : large
              ? { xs: 3, md: 3.5 }
              : 2.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {c.meta && (
            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: 700,
                fontSize: "0.92rem",
                mb: 1,
              }}
            >
              {c.meta}
            </Typography>
          )}
          <Typography
            variant={lead ? "h3" : large ? "h4" : "h5"}
            component="h3"
            sx={{
              fontFamily: "windroos, sans-serif",
              color: COLORS.blue,
              mb: 1,
              fontSize: lead
                ? "clamp(1.8rem, 3vw, 2.4rem)"
                : large
                ? { xs: "1.6rem", md: "1.8rem" }
                : undefined,
              lineHeight: 1.12,
              ...(lead
                ? {}
                : {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }),
            }}
          >
            {c.heading}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.muted,
              fontSize: lead ? "1rem" : undefined,
              ...(lead
                ? {}
                : {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }),
            }}
          >
            {c.body}
          </Typography>
          {c.ctaLabel && primaryHref && (
            <Box sx={{ mt: "auto", pt: 2 }}>
              <Button
                {...(primaryIsRoute
                  ? { component: Link, to: primaryHref }
                  : { component: "a", href: primaryHref, target: primaryTarget, rel: primaryTarget ? "noopener noreferrer" : undefined })}
                size="small"
                sx={{ color: COLORS.red, m: 0, p: 0, fontWeight: 700 }}
              >
                {primaryUsesDocumentRoute ? `${c.ctaLabel} (via documenten)` : c.ctaLabel} →
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function SocialHighlight({ block }) {
  const c = block.content || {};
  const href = c.externalUrl;
  const isExternal = href && /^https?:\/\//i.test(href);
  const linkProps = isExternal
    ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
    : { component: "div" };
  return (
    <Box
      {...linkProps}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: 1.5,
        overflow: "hidden",
        backgroundColor: "#fff",
        border: "1px solid rgba(32, 48, 51, 0.14)",
        boxShadow: "0 8px 22px rgba(32, 48, 51, 0.08)",
        textDecoration: "none",
        color: "inherit",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": isExternal
          ? {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 30px rgba(32, 48, 51, 0.14)",
            }
          : undefined,
      }}
    >
      {c.s3Key && (
        <Box sx={{ aspectRatio: "1 / 1", overflow: "hidden" }}>
          <SignedImage s3Key={c.s3Key} alt={c.imageAlt || c.caption || "Instagram-moment"} />
        </Box>
      )}
      <Box sx={{ p: 1.75, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: COLORS.red }}>
          <InstagramIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.02em" }}>
            {c.account || "@windrooswbd"}
          </Typography>
        </Box>
        {c.caption && (
          <Typography
            variant="body2"
            sx={{
              color: "#304346",
              fontSize: "0.88rem",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
              overflow: "hidden",
            }}
          >
            {c.caption}
          </Typography>
        )}
        {isExternal && (
          <Typography
            sx={{
              mt: "auto",
              pt: 0.5,
              fontSize: "0.78rem",
              fontWeight: 700,
              color: COLORS.red,
            }}
          >
            Bekijk op Instagram →
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SocialStrip({ blocks }) {
  if (!blocks.length) return null;
  return (
    <Box sx={{ mt: { xs: 5, md: 6 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          color: COLORS.blue,
        }}
      >
        <InstagramIcon sx={{ fontSize: 22 }} />
        <Typography
          variant="overline"
          sx={{
            color: COLORS.blue,
            letterSpacing: "0.12em",
            fontWeight: 700,
            fontSize: "0.82rem",
          }}
        >
          Uit de school · @windrooswbd
        </Typography>
        <ContentReviewChip status="controleer" sx={{ ml: 1 }} />
      </Box>
      <Typography
        sx={{
          fontFamily: "windroos, Georgia, serif",
          color: "#1f3a3d",
          fontSize: { xs: "1.4rem", md: "1.6rem" },
          mb: 1.5,
        }}
      >
        Momenten uit de klas
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, md: 2.5 },
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {blocks.map((block) => (
          <SocialHighlight key={block.id} block={block} />
        ))}
      </Box>
      <Typography
        sx={{
          mt: 2,
          fontSize: "0.82rem",
          color: COLORS.muted,
          fontStyle: "italic",
        }}
      >
        Gecureerde momenten uit het schoolleven. Voor de meest actuele berichten,
        bezoek <Box component="a" href="https://www.instagram.com/windrooswbd/" target="_blank" rel="noopener noreferrer" sx={{ color: COLORS.red, fontWeight: 700, textDecoration: "none" }}>@windrooswbd op Instagram</Box>.
      </Typography>
    </Box>
  );
}

export default function NewsGridSection({ section }) {
  const { content, blocks = [] } = section;

  // Split news from other blocks (vacancy etc.) so the news path stays
  // chronological and vacancy never silently steals the lead position.
  // social-highlight blocks render in their own "Uit de school" strip
  // below the main news grid (UAT 2026-05-24, curated Instagram MVP).
  const newsBlocksSorted = getHomepageNewsBlocks(blocks);
  const socialBlocksSorted = blocks
    .filter((b) => b.blockType === "social-highlight" && b.isActive !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const otherBlocksSorted = blocks
    .filter(
      (b) =>
        b.blockType !== "news-highlight" &&
        b.blockType !== "social-highlight" &&
        b.blockType !== "vacancy-highlight"
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const sorted = [...newsBlocksSorted, ...otherBlocksSorted];
  const heroIndex = newsBlocksSorted.length > 0 ? 0 : -1;
  const useLeadLayout = heroIndex >= 0;

  // Numbering for news cards skips the lead (which is already prominent) and
  // labels the remaining news items from 2 onward, so the route reads as
  // "newest article, then second, then third".
  let newsCounter = 0;

  return (
    <Box
      id="nieuws"
      component="section"
      sx={{
        py: "clamp(48px, 7vw, 92px)",
        px: "clamp(20px, 5vw, 72px)",
        backgroundColor: "#e7f5f7",
        scrollMarginTop: { xs: 64, md: 80 },
      }}
    >
      <Container disableGutters sx={{ maxWidth: { md: 1160 } }} maxWidth={false}>
        <SectionHeader
          eyebrow={content.eyebrow}
          heading={content.heading}
          lead={content.lead}
          reviewStatus="controleer"
        />
        <Box
          sx={{
            position: "relative",
            mt: 4,
            pl: { xs: "20px", md: 0 },
            pt: { xs: "12px", md: "20px" },
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              borderLeft: `4px dashed ${PATH_LINE_COLOR}`,
              display: { xs: "block", md: "none" },
              zIndex: 0,
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: "6%",
              right: "6%",
              top: useLeadLayout ? "62%" : "12px",
              borderTop: `4px dashed ${PATH_LINE_COLOR}`,
              borderRadius: "50%",
              transform: "rotate(-1.4deg)",
              display: { xs: "none", md: "block" },
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "grid",
              gap: { xs: 3, md: 3 },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gridAutoRows: { md: "minmax(0, auto)" },
            }}
          >
            {sorted.map((block, idx) => {
              if (block.blockType !== "news-highlight") {
                return null;
              }
              const isHero = idx === heroIndex;
              const isLead = isHero && useLeadLayout;
              const stepNumber = newsCounter + 1;
              newsCounter += 1;
              const stepLabel = isLead ? undefined : String(stepNumber);
              const stepAccent = stepNumber % 2 === 1 ? "orange" : "blue";
              return (
                <NewsHighlight
                  key={block.id}
                  block={block}
                  large={isHero}
                  lead={isLead}
                  stepLabel={stepLabel}
                  stepAccent={stepAccent}
                />
              );
            })}
          </Box>
        </Box>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            component={Link}
            to="/nieuws"
            sx={{
              m: 0,
              color: COLORS.red,
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            Bekijk al het nieuws →
          </Button>
        </Box>
        <SocialStrip blocks={socialBlocksSorted} />
      </Container>
    </Box>
  );
}
